/**
 * Retry whole transactions after a confirmed rollback or a pre-commit leader loss.
 * The tracked proxy reconciles lost COMMIT acknowledgements against durable receipts:
 * an applied commit returns its saved result, and a missing receipt permits replay.
 * Unreconciled legacy commit errors remain marked not replayable.
 *
 * This module stays browser-independent for wrap-actor-database server imports.
 */
import { isLeaderChangedError } from "@-/dev.improve.haniverse.lib.leader-resource/errors";

/**
 * Stamped onto a `LeaderChangedError` whose lost statement was a `commit`. A
 * plain property (not a subclass) because the error object is minted by the
 * request queue, which knows no SQL — the SQL-aware caller annotates it after
 * the fact.
 */
const NOT_REPLAYABLE_MARKER = "__haniverseLeaderChangeNotReplayable";

/** How far up a `cause` chain to look — drizzle re-wraps driver errors. */
const MAX_CAUSE_DEPTH = 5;

/**
 * A leader change can hit the same transaction at most once per handover, and
 * a handover is rare; three attempts covers a leader that dies twice in a row
 * without letting a pathological election loop spin forever.
 */
export const DEFAULT_LEADER_CHANGE_REPLAY_ATTEMPTS = 3;

export function markLeaderChangeNotReplayable(error: unknown): void {
  if (typeof error === "object" && error !== null) {
    Reflect.set(error, NOT_REPLAYABLE_MARKER, true);
  }
}

/**
 * Is `error` (or anything in its `cause` chain) a leader change whose
 * transaction can be safely run again from the top?
 */
export function isReplayableLeaderChangeError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    if (typeof current !== "object" || current === null) {
      return false;
    }
    if (isLeaderChangedError(current)) {
      return Reflect.get(current, NOT_REPLAYABLE_MARKER) !== true;
    }
    current = Reflect.get(current, "cause");
  }
  return false;
}

/**
 * Is `error` (or anything in its `cause` chain) a leader change that lost a `commit`'s
 * acknowledgement? Unlike every other transaction failure, the transaction may have landed: only
 * the caller can settle it, by replaying an operation it knows to be idempotent.
 */
export function isLostCommitLeaderChangeError(error: unknown): boolean {
  let current: unknown = error;
  for (let depth = 0; depth < MAX_CAUSE_DEPTH; depth += 1) {
    if (typeof current !== "object" || current === null) {
      return false;
    }
    if (isLeaderChangedError(current)) {
      return Reflect.get(current, NOT_REPLAYABLE_MARKER) === true;
    }
    current = Reflect.get(current, "cause");
  }
  return false;
}

export interface LeaderChangeReplayOptions {
  /** Total attempts, including the first. Defaults to {@link DEFAULT_LEADER_CHANGE_REPLAY_ATTEMPTS}. */
  maxAttempts?: number;
  onRetry?(info: { attempt: number; error: unknown }): void;
}

/**
 * Run `operation` again when it fails with a replayable leader change.
 *
 * No backoff, deliberately: the proxied connection already queues every
 * statement until the new leader has successfully opened, so an immediate
 * retry simply lands at the head of that queue. Sleeping here would only add
 * latency on top of the handover the user is already waiting out.
 */
export async function retryOnLeaderChange<T>(
  operation: () => Promise<T>,
  options: LeaderChangeReplayOptions = {},
): Promise<T> {
  const maxAttempts = options.maxAttempts ?? DEFAULT_LEADER_CHANGE_REPLAY_ATTEMPTS;
  for (let attempt = 1; ; attempt += 1) {
    try {
      return await operation();
    } catch (error) {
      if (attempt >= maxAttempts || !isReplayableLeaderChangeError(error)) {
        throw error;
      }
      options.onRetry?.({ attempt, error });
    }
  }
}
